import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Mail, 
  BarChart3, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  EyeOff,
  Save,
  X,
  ChevronRight,
  MessageSquare,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [posts, setPosts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Post editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Finance',
    featured_image: '',
    published: false
  });

  const categories = ['Emerging Topics', 'Finance', 'Fintech', 'Regulatory', 'Education', 'Design', 'Culture'];

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [postsRes, subscribersRes, messagesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/posts?published_only=false`, { withCredentials: true }),
        axios.get(`${API}/newsletter/subscribers`, { withCredentials: true }),
        axios.get(`${API}/contact/messages`, { withCredentials: true }),
        axios.get(`${API}/analytics`, { withCredentials: true })
      ]);
      
      setPosts(postsRes.data);
      setSubscribers(subscribersRes.data);
      setMessages(messagesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setPostForm({
      ...postForm,
      title,
      slug: editingPost ? postForm.slug : generateSlug(title)
    });
  };

  const handleSavePost = async () => {
    if (!postForm.title || !postForm.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.post_id}`, postForm, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/posts`, postForm, { withCredentials: true });
        toast.success('Post created');
      }
      
      setShowEditor(false);
      setEditingPost(null);
      setPostForm({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'Finance',
        featured_image: '',
        published: false
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      featured_image: post.featured_image || '',
      published: post.published
    });
    setShowEditor(true);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/messages/${messageId}/read`, {}, { withCredentials: true });
      setMessages(messages.map(m => 
        m.message_id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'posts', label: 'Posts', icon: FileText },
    { id: 'subscribers', label: 'Subscribers', icon: Users },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ];

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <section className="bg-white py-6 border-b border-primary/10">
        <div className="container-padding">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-sans font-normal text-primary">Admin Dashboard</h1>
            <Button 
              onClick={() => { setEditingPost(null); setShowEditor(true); }}
              className="btn-primary rounded-none"
              data-testid="new-post-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>
      </section>

      <div className="container-padding py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-3">
            <nav className="dashboard-panel p-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-sans transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-accent/10 text-accent' 
                      : 'text-secondary hover:bg-primary/5'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {loading ? (
              <div className="dashboard-panel p-8 text-center">
                <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="dashboard-panel p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <Eye className="w-5 h-5 text-accent" />
                          <span className="label-uppercase">Total Views</span>
                        </div>
                        <p className="text-3xl font-sans font-light text-primary">
                          {analytics?.total_views?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="dashboard-panel p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="w-5 h-5 text-accent" />
                          <span className="label-uppercase">Posts</span>
                        </div>
                        <p className="text-3xl font-sans font-light text-primary">
                          {analytics?.total_posts || 0}
                        </p>
                      </div>
                      <div className="dashboard-panel p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <Users className="w-5 h-5 text-accent" />
                          <span className="label-uppercase">Subscribers</span>
                        </div>
                        <p className="text-3xl font-sans font-light text-primary">
                          {analytics?.total_subscribers || 0}
                        </p>
                      </div>
                      <div className="dashboard-panel p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <Mail className="w-5 h-5 text-accent" />
                          <span className="label-uppercase">Messages</span>
                        </div>
                        <p className="text-3xl font-sans font-light text-primary">
                          {analytics?.total_messages || 0}
                        </p>
                      </div>
                    </div>

                    {/* Recent Posts Performance */}
                    <div className="dashboard-panel">
                      <h3 className="text-lg font-sans font-medium text-primary mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-accent" />
                        Recent Posts Performance
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead className="text-right">Views</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analytics?.recent_views?.slice(0, 5).map((post) => (
                            <TableRow key={post.title}>
                              <TableCell className="font-medium">{post.title}</TableCell>
                              <TableCell className="text-right">{post.views}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Posts Tab */}
                {activeTab === 'posts' && (
                  <div className="dashboard-panel">
                    <h3 className="text-lg font-sans font-medium text-primary mb-4">All Posts</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Views</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {posts.map((post) => (
                          <TableRow key={post.post_id} data-testid={`post-row-${post.post_id}`}>
                            <TableCell className="font-medium max-w-xs truncate">{post.title}</TableCell>
                            <TableCell>{post.category}</TableCell>
                            <TableCell>
                              {post.published ? (
                                <span className="inline-flex items-center gap-1 text-accent text-sm">
                                  <Eye className="w-3 h-3" /> Published
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-secondary text-sm">
                                  <EyeOff className="w-3 h-3" /> Draft
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{post.views}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditPost(post)}
                                  data-testid={`edit-post-${post.post_id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePost(post.post_id)}
                                  className="text-destructive"
                                  data-testid={`delete-post-${post.post_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Subscribers Tab */}
                {activeTab === 'subscribers' && (
                  <div className="dashboard-panel">
                    <h3 className="text-lg font-sans font-medium text-primary mb-4">
                      Newsletter Subscribers ({subscribers.length})
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Subscribed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscribers.map((sub) => (
                          <TableRow key={sub.subscriber_id}>
                            <TableCell className="font-medium">{sub.email}</TableCell>
                            <TableCell>{sub.name || '-'}</TableCell>
                            <TableCell>{formatDate(sub.subscribed_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Messages Tab */}
                {activeTab === 'messages' && (
                  <div className="dashboard-panel">
                    <h3 className="text-lg font-sans font-medium text-primary mb-4">
                      Contact Messages ({messages.filter(m => !m.read).length} unread)
                    </h3>
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div 
                          key={msg.message_id}
                          className={`p-4 border ${msg.read ? 'border-primary/10 bg-background' : 'border-accent/30 bg-accent/5'}`}
                          data-testid={`message-${msg.message_id}`}
                        >
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <p className="font-sans font-medium text-primary">{msg.name}</p>
                              <p className="text-sm text-secondary">{msg.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-secondary">{formatDate(msg.created_at)}</span>
                              {!msg.read && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkMessageRead(msg.message_id)}
                                >
                                  Mark Read
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-secondary font-serif">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Post Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-sans">
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-uppercase mb-2 block">Title</label>
                <Input
                  value={postForm.title}
                  onChange={handleTitleChange}
                  placeholder="Post title"
                  className="rounded-none"
                  data-testid="post-title-input"
                />
              </div>
              <div>
                <label className="label-uppercase mb-2 block">Slug</label>
                <Input
                  value={postForm.slug}
                  onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })}
                  placeholder="post-slug"
                  className="rounded-none"
                  data-testid="post-slug-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-uppercase mb-2 block">Category</label>
                <Select
                  value={postForm.category}
                  onValueChange={(value) => setPostForm({ ...postForm, category: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="post-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-uppercase mb-2 block">Featured Image URL</label>
                <Input
                  value={postForm.featured_image}
                  onChange={(e) => setPostForm({ ...postForm, featured_image: e.target.value })}
                  placeholder="https://..."
                  className="rounded-none"
                  data-testid="post-image-input"
                />
              </div>
            </div>

            <div>
              <label className="label-uppercase mb-2 block">Excerpt</label>
              <Textarea
                value={postForm.excerpt}
                onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                placeholder="Brief description of the post..."
                className="rounded-none"
                rows={3}
                data-testid="post-excerpt-input"
              />
            </div>

            <div>
              <label className="label-uppercase mb-2 block">Content (HTML)</label>
              <Textarea
                value={postForm.content}
                onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                placeholder="<p>Your post content here...</p>"
                className="rounded-none font-mono text-sm"
                rows={12}
                data-testid="post-content-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  checked={postForm.published}
                  onCheckedChange={(checked) => setPostForm({ ...postForm, published: checked })}
                  data-testid="post-published-switch"
                />
                <label className="text-sm font-sans">Publish immediately</label>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEditor(false)}
                  className="rounded-none"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePost}
                  className="btn-primary rounded-none"
                  data-testid="save-post-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingPost ? 'Update Post' : 'Create Post'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
